import type * as React from "react";

import { cn } from "../lib/cn";

export interface FieldErrorProps extends Omit<
  React.HTMLAttributes<HTMLParagraphElement>,
  "aria-live"
> {
  /**
   * What went wrong and what to do about it, in Brian's voice. Nothing here
   * renders nothing at all, so `{error && ...}` guards are unnecessary.
   */
  children?: React.ReactNode;
  ref?: React.Ref<HTMLParagraphElement>;
}

/**
 * The line under a control that says the write did not go through: a mutation
 * came back with an error and the person is still standing in front of the
 * thing they tried to change.
 *
 * It exists for the `aria-live`. Announcing a failure is not optional, and left
 * to each call site it was carried by some and forgotten by others - a silent
 * failure for anyone not watching that part of the screen. `aria-live` is baked
 * in after the prop spread, so it cannot be dropped. `polite` rather than an
 * alert: the person just pressed the button, so they are already here.
 *
 * Not the only way the product reports a failure, and the narrowest:
 * - `FieldError` is one control's write failing, written where the control is.
 * - `PanelError` is a whole panel's fetch giving up: it replaces the panel's
 *   contents and offers a retry.
 * - The app's `WriteErrorNotice` (a `MessageSlip`) is the catch-all for writes
 *   with no error UI of their own, left in the corner of the desk.
 *
 * If the control it belongs to is a form field, point that field's
 * `aria-describedby` at this element's `id` as well, so the message is read
 * when focus lands on the field and not only when it appears.
 *
 * No margin is baked in: half the call sites sit in a `gap-*` stack where one
 * would be wrong. Pass `mt-2` where the error hangs off the control above it.
 */
export function FieldError({ children, className, ...rest }: FieldErrorProps) {
  // An empty live region is not free: it still takes the caller's margin, and
  // there is nothing to announce until there is a message.
  if (children == null || children === false || children === "") return null;

  return (
    <p
      {...rest}
      aria-live="polite"
      className={cn("text-sm text-ink-stopped", className)}
    >
      {children}
    </p>
  );
}
